import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  FiArrowLeft, FiPlus, FiTrash2, FiMove, FiEye, FiEyeOff, FiSave, 
  FiExternalLink, FiLink, FiCopy, FiDownload, FiGrid, FiZap, FiMail
} from "react-icons/fi";
import { 
  FaInstagram, FaFacebookF, FaYoutube, FaLinkedinIn, FaGithub, 
  FaTiktok, FaTwitter, FaGlobe, FaSpotify, FaWhatsapp, FaTelegram, FaSnapchatGhost, FaEnvelope
} from "react-icons/fa";
import { QRCodeCanvas } from "qrcode.react";
import { useToast } from "../components/ToastProvider";
import { api } from "../utils/api";
import { getBaseUrl } from "../utils/domain";
import BioTemplateGallery from "../components/BioTemplateGallery";
import { COUNTRY_OPTIONS, TEMPLATE_TAGS, appendTemplateTag } from "../utils/templateHelpers";
import "../styles/Dashboard.css";
import "../styles/Modal.css";
import "../styles/BioPages.css";

const SOCIAL_PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: FaInstagram, color: "#E4405F", placeholder: "https://instagram.com/yourhandle" },
  { key: "facebook", label: "Facebook", icon: FaFacebookF, color: "#1877F2", placeholder: "https://facebook.com/yourpage" },
  { key: "youtube", label: "YouTube", icon: FaYoutube, color: "#FF0000", placeholder: "https://youtube.com/@yourchannel" },
  { key: "whatsapp", label: "WhatsApp", icon: FaWhatsapp, color: "#25D366", placeholder: "https://wa.me/1234567890" },
  { key: "telegram", label: "Telegram", icon: FaTelegram, color: "#0088cc", placeholder: "https://t.me/yourhandle" },
  { key: "tiktok", label: "TikTok", icon: FaTiktok, color: "#000000", placeholder: "https://tiktok.com/@yourhandle" },
  { key: "snapchat", label: "Snapchat", icon: FaSnapchatGhost, color: "#FFFC00", placeholder: "https://snapchat.com/add/yourhandle" },
  { key: "spotify", label: "Spotify", icon: FaSpotify, color: "#1DB954", placeholder: "https://open.spotify.com/user/xxx" },
  { key: "twitter", label: "X (Twitter)", icon: FaTwitter, color: "#000000", placeholder: "https://x.com/yourhandle" },
  { key: "linkedin", label: "LinkedIn", icon: FaLinkedinIn, color: "#0A66C2", placeholder: "https://linkedin.com/in/yourhandle" },
  { key: "github", label: "GitHub", icon: FaGithub, color: "#181717", placeholder: "https://github.com/yourhandle" },
  { key: "email", label: "Email", icon: FaEnvelope, color: "#EA4335", placeholder: "mailto:you@example.com" },
  { key: "website", label: "Website", icon: FaGlobe, color: "#2383e2", placeholder: "https://yourwebsite.com" },
];

const THEME_PRESETS = [
  { id: "light", bg: "#f7f7f5", text: "#37352f", button: "#37352f", buttonText: "#ffffff" },
  { id: "sky", bg: "#e0f2fe", text: "#0c4a6e", button: "#0284c7", buttonText: "#ffffff" },
  { id: "mint", bg: "#d1fae5", text: "#064e3b", button: "#059669", buttonText: "#ffffff" },
  { id: "sunset", bg: "#fef3c7", text: "#78350f", button: "#f59e0b", buttonText: "#000000" },
  { id: "rose", bg: "#fce7f3", text: "#831843", button: "#ec4899", buttonText: "#ffffff" },
  { id: "navy", bg: "#1e3a5f", text: "#ffffff", button: "#3b82f6", buttonText: "#ffffff" },
  { id: "ocean", bg: "#134e5e", text: "#ffffff", button: "#0d9488", buttonText: "#ffffff" },
  { id: "dark", bg: "#191919", text: "#ffffff", button: "#ffffff", buttonText: "#191919" },
];

const BUTTON_SHAPES = [
  { id: "rounded-none", radius: "0" },
  { id: "rounded-sm", radius: "4px" },
  { id: "rounded-md", radius: "8px" },
  { id: "rounded-lg", radius: "12px" },
  { id: "rounded-full", radius: "9999px" },
];

const createSmartCta = () => ({
  enabled: false,
  title: "Featured action",
  description: "Highlight your most important destination with device and country-aware routing.",
  button_label: "Open",
  default_url: "",
  mobile_url: "",
  country_overrides: [],
});

const createLeadCapture = (theme = "light") => ({
  enabled: false,
  title: "Stay in the loop",
  description: "Capture emails directly from the bio page.",
  button_label: "Join",
  success_message: "Thanks, you're in.",
  cardBackgroundColor: theme === "dark" ? "#252525" : "#ffffff",
  titleColor: theme === "dark" ? "#ffffff" : "#37352f",
  descriptionColor: theme === "dark" ? "#9b9a97" : "#6b6b6b",
  inputBackgroundColor: theme === "dark" ? "#191919" : "#f7f7f5",
  inputTextColor: theme === "dark" ? "#ffffff" : "#37352f",
  inputBorderColor: theme === "dark" ? "#373737" : "#e9e9e7",
  buttonColor: theme === "dark" ? "#ffffff" : "#37352f",
  buttonTextColor: theme === "dark" ? "#191919" : "#ffffff",
});

const getLeadCaptureThemeColors = (theme = "light") => createLeadCapture(theme);

export default function BioPageEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { addToast } = useToast();
  const qrPreviewRef = useRef(null);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [domains, setDomains] = useState([]);
  const [activeTab, setActiveTab] = useState("build");
  const [showTemplates, setShowTemplates] = useState(isNew);
  const [showMoreSocials, setShowMoreSocials] = useState(false);

  // Form state
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [theme, setTheme] = useState("light");
  const [domain, setDomain] = useState("");
  const [links, setLinks] = useState([]);
  const [socialLinks, setSocialLinks] = useState({});
  const [dragIndex, setDragIndex] = useState(null);
  const [smartCta, setSmartCta] = useState(createSmartCta());
  const [leadCapture, setLeadCapture] = useState(createLeadCapture());

  // Style customization
  const [style, setStyle] = useState({
    buttonShape: "rounded-md",
    buttonColor: "#37352f",
    buttonTextColor: "#ffffff",
    backgroundColor: "#f7f7f5",
    textColor: "#37352f",
  });

  const fetchData = useCallback(async () => {
    try {
      const domainsData = await api.getDomains();
      setDomains((domainsData.domains || []).filter(d => d.status === "active"));

      if (!isNew) {
        const bioPage = await api.getBioPage(id);
        setSlug(bioPage.slug || "");
        setTitle(bioPage.title || "");
        setDescription(bioPage.description || "");
        setAvatarUrl(bioPage.avatar_url || "");
        setTheme(bioPage.theme || "light");
        setDomain(bioPage.domain || "");
        setLinks((bioPage.links || []).map((link, index) => ({
          title: link.title || "",
          url: link.url || "",
          icon: link.icon || "",
          enabled: link.enabled !== false,
          section: link.section || "",
          order: link.order ?? index,
        })));
        setSocialLinks(bioPage.social_links || {});
        setSmartCta({ ...createSmartCta(), ...(bioPage.smart_cta || {}) });
        setLeadCapture({ ...createLeadCapture(bioPage.theme || "light"), ...(bioPage.lead_capture || {}) });
        // Load saved style if it exists, otherwise use defaults based on theme
        if (bioPage.style && typeof bioPage.style === "object") {
          setStyle({
            buttonShape: bioPage.style.buttonShape || "rounded-md",
            buttonColor: bioPage.style.buttonColor || (bioPage.theme === "dark" ? "#ffffff" : "#37352f"),
            buttonTextColor: bioPage.style.buttonTextColor || (bioPage.theme === "dark" ? "#191919" : "#ffffff"),
            backgroundColor: bioPage.style.backgroundColor || (bioPage.theme === "dark" ? "#191919" : "#f7f7f5"),
            textColor: bioPage.style.textColor || (bioPage.theme === "dark" ? "#ffffff" : "#37352f"),
          });
        } else if (bioPage.theme === "dark") {
          setStyle({
            buttonShape: "rounded-md",
            buttonColor: "#ffffff",
            buttonTextColor: "#191919",
            backgroundColor: "#191919",
            textColor: "#ffffff",
          });
        }
      }
    } catch (err) {
      console.error(err);
      addToast("Failed to load data", "error");
      navigate("/bio");
    } finally {
      setLoading(false);
    }
  }, [isNew, id, addToast, navigate]);

  useEffect(() => {
    document.title = isNew ? "Create Bio Page - LinkSquire" : "Edit Bio Page - LinkSquire";
    fetchData();
  }, [fetchData, isNew]);

  const handleSelectTemplate = (template) => {
    setTitle(template.title);
    setDescription(template.description);
    setLinks(template.links.map((l, i) => ({ ...l, order: i, section: l.section || "" })));
    setSocialLinks(template.social_links);
    setTheme(template.theme);
    if (template.style) {
      setStyle(template.style);
    }
    setShowTemplates(false);
  };

  const getPreviewUrl = () => {
    const base = domain ? `https://${domain}` : getBaseUrl();
    return `${base}/${slug || "your-slug"}`;
  };

  const addLink = () => {
    setLinks([...links, { title: "", url: "", icon: "", enabled: true, section: "" }]);
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setLinks(newLinks);
  };

  const addSmartCtaOverride = () => {
    setSmartCta({
      ...smartCta,
      country_overrides: [...(smartCta.country_overrides || []), { country: "US", url: "" }],
    });
  };

  const updateSmartCtaOverride = (index, field, value) => {
    const overrides = [...(smartCta.country_overrides || [])];
    overrides[index] = { ...overrides[index], [field]: field === "country" ? value.toUpperCase() : value };
    setSmartCta({ ...smartCta, country_overrides: overrides });
  };

  const removeSmartCtaOverride = (index) => {
    setSmartCta({
      ...smartCta,
      country_overrides: (smartCta.country_overrides || []).filter((_, i) => i !== index),
    });
  };

  const copyPreviewUrl = async () => {
    try {
      await navigator.clipboard.writeText(getPreviewUrl());
      addToast("Preview URL copied", "success");
    } catch {
      addToast("Failed to copy preview URL", "error");
    }
  };

  const downloadQrCode = () => {
    const canvas = qrPreviewRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${slug || "bio-page"}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const insertSmartCtaTag = (field, tag) => {
    setSmartCta((current) => ({
      ...current,
      [field]: appendTemplateTag(current[field] || "", tag),
    }));
  };

  const insertSmartCtaOverrideTag = (index, tag) => {
    setSmartCta((current) => ({
      ...current,
      country_overrides: (current.country_overrides || []).map((override, overrideIndex) => (
        overrideIndex === index
          ? { ...override, url: appendTemplateTag(override.url || "", tag) }
          : override
      )),
    }));
  };

  const removeLink = (index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const toggleLinkEnabled = (index) => {
    const newLinks = [...links];
    newLinks[index] = { ...newLinks[index], enabled: !newLinks[index].enabled };
    setLinks(newLinks);
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    
    const newLinks = [...links];
    const draggedItem = newLinks[dragIndex];
    newLinks.splice(dragIndex, 1);
    newLinks.splice(index, 0, draggedItem);
    setLinks(newLinks);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const toggleSocialLink = (key) => {
    if (socialLinks.hasOwnProperty(key)) {
      const newSocials = { ...socialLinks };
      delete newSocials[key];
      setSocialLinks(newSocials);
    } else {
      setSocialLinks({ ...socialLinks, [key]: "" });
    }
  };

  const updateSocialLink = (key, value) => {
    setSocialLinks({ ...socialLinks, [key]: value });
  };

  const applyThemePreset = (preset) => {
    const leadCaptureThemeColors = getLeadCaptureThemeColors(preset.id === "dark" || preset.id === "navy" || preset.id === "ocean" ? "dark" : "light");
    setStyle({
      ...style,
      backgroundColor: preset.bg,
      textColor: preset.text,
      buttonColor: preset.button,
      buttonTextColor: preset.buttonText,
    });
    setLeadCapture((current) => ({
      ...current,
      ...leadCaptureThemeColors,
      enabled: current.enabled,
      title: current.title,
      description: current.description,
      button_label: current.button_label,
      success_message: current.success_message,
    }));
    setTheme(preset.id === "dark" || preset.id === "navy" || preset.id === "ocean" ? "dark" : "light");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!slug || slug.length < 3) {
      addToast("Slug must be at least 3 characters", "error");
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(slug)) {
      addToast("Slug can only contain letters, numbers, hyphens, and underscores", "error");
      return;
    }
    if (!title) {
      addToast("Title is required", "error");
      return;
    }

    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      if (link.title && !link.url) {
        addToast(`Link "${link.title}": URL is required`, "error");
        return;
      }
      if (link.url) {
        try {
          new URL(link.url);
        } catch {
          addToast(`Link ${i + 1}: Invalid URL`, "error");
          return;
        }
      }
    }

    setSaving(true);
    try {
      const filteredLinks = links.filter(l => l.title && l.url).map((link, index) => ({
        ...link,
        section: (link.section || "").trim(),
        order: index,
      }));
      const payload = {
        slug: slug.toLowerCase(),
        title,
        description,
        avatar_url: avatarUrl,
        theme,
        domain,
        links: filteredLinks,
        social_links: socialLinks,
        style: style, // Include design customization
        smart_cta: smartCta,
        lead_capture: leadCapture,
      };

      if (isNew) {
        await api.createBioPage(payload);
        addToast("Bio page created!", "success");
      } else {
        await api.updateBioPage(id, payload);
        addToast("Bio page updated!", "success");
      }
      navigate("/bio");
    } catch (err) {
      addToast(err.message || "Failed to save bio page", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show template gallery for new pages
  if (showTemplates && isNew) {
    return (
      <div className="dashboard">
        <div style={{ marginBottom: 20 }}>
          <button className="btn-secondary" onClick={() => navigate("/bio")}>
            <FiArrowLeft /> Back to Bio Pages
          </button>
        </div>
        <BioTemplateGallery onSelectTemplate={handleSelectTemplate} />
      </div>
    );
  }

  const visibleSocials = showMoreSocials ? SOCIAL_PLATFORMS : SOCIAL_PLATFORMS.slice(0, 8);

  return (
    <div className="dashboard" style={{ maxWidth: 1200 }}>
      <div className="dashboard-header" style={{ marginBottom: 0 }}>
        <button className="btn-secondary" onClick={() => navigate("/bio")} style={{ marginRight: 16 }}>
          <FiArrowLeft /> Back
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 24 }}>{isNew ? "Create Bio Page" : "Edit Bio Page"}</h1>
        </div>
        <button 
          className="btn-primary" 
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? <span className="spinner-sm" /> : <FiSave />} {saving ? "Saving..." : "Publish Changes"}
        </button>
      </div>

      {/* Tabs */}
      <div className="bio-editor-tabs">
        <button 
          className={`bio-editor-tab ${activeTab === "build" ? "active" : ""}`}
          onClick={() => setActiveTab("build")}
        >
          Build
        </button>
        <button 
          className={`bio-editor-tab ${activeTab === "design" ? "active" : ""}`}
          onClick={() => setActiveTab("design")}
        >
          Design
        </button>
        <button 
          className={`bio-editor-tab ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
        >
          Settings
        </button>
      </div>

      <div className="bio-editor-layout">
        {/* Editor Panel */}
        <div>
          {activeTab === "build" && (
            <>
              {/* Add Link Button */}
              <button className="add-link-btn" onClick={addLink} style={{ marginBottom: 20 }}>
                <FiPlus /> Add Link
              </button>
              <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
                Use the optional section field to group links into clean content blocks on the live page.
              </p>

              {/* Links Section */}
              {links.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  {links.map((link, index) => (
                    <div
                      key={index}
                      className={`bio-link-item ${dragIndex === index ? "dragging" : ""}`}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      style={{ opacity: link.enabled ? 1 : 0.5 }}
                    >
                      <div className="bio-link-item-header">
                        <div className="bio-link-item-drag">
                          <FiMove size={16} />
                        </div>
                        <div className="bio-link-item-icon">
                          <FiLink size={16} />
                        </div>
                        <div className="bio-link-item-content">
                          <input
                            type="text"
                            value={link.title}
                            onChange={(e) => updateLink(index, "title", e.target.value)}
                            placeholder="Link title"
                            className="bio-link-item-title-input"
                          />
                          <input
                            type="url"
                            value={link.url}
                            onChange={(e) => updateLink(index, "url", e.target.value)}
                            placeholder="https://example.com"
                            className="bio-link-item-url-input"
                          />
                          <input
                            type="text"
                            value={link.section || ""}
                            onChange={(e) => updateLink(index, "section", e.target.value)}
                            placeholder="Section label, e.g. Shop"
                            className="bio-link-item-url-input"
                          />
                        </div>
                        <div className="bio-link-item-actions">
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => toggleLinkEnabled(index)}
                            data-tooltip={link.enabled ? "Disable" : "Enable"}
                          >
                            {link.enabled ? <FiEye /> : <FiEyeOff />}
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-icon-danger"
                            onClick={() => removeLink(index)}
                            data-tooltip="Remove"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Social Icons Section */}
              <div className="bio-editor-section">
                <h3 className="bio-editor-section-title">Social Icons</h3>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
                  Select your social platforms. They'll appear as icons below your links.
                </p>
                <div className="social-icons-grid">
                  {visibleSocials.map(platform => {
                    const Icon = platform.icon;
                    const isActive = socialLinks[platform.key] !== undefined;
                    return (
                      <button
                        key={platform.key}
                        type="button"
                        className={`social-icon-btn ${isActive ? "active" : ""}`}
                        onClick={() => toggleSocialLink(platform.key)}
                        style={isActive ? { borderColor: platform.color, color: platform.color } : {}}
                      >
                        <Icon style={isActive ? { color: platform.color } : {}} />
                        {platform.label}
                      </button>
                    );
                  })}
                </div>
                {!showMoreSocials && (
                  <button 
                    className="btn-secondary" 
                    onClick={() => setShowMoreSocials(true)}
                    style={{ marginTop: 8 }}
                  >
                    <FiPlus /> View more socials
                  </button>
                )}

                {/* Active social link inputs */}
                {Object.keys(socialLinks).length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Edit your links</h4>
                    {SOCIAL_PLATFORMS.filter(p => socialLinks[p.key] !== undefined).map(platform => {
                      const Icon = platform.icon;
                      return (
                        <div key={platform.key} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                          <div style={{ 
                            width: 36, 
                            height: 36, 
                            borderRadius: 8, 
                            background: "var(--surface-hover)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}>
                            <Icon style={{ color: platform.color }} />
                          </div>
                          <input
                            type="url"
                            value={socialLinks[platform.key] || ""}
                            onChange={(e) => updateSocialLink(platform.key, e.target.value)}
                            placeholder={platform.placeholder}
                            style={{ flex: 1, padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 14 }}
                          />
                          <button
                            type="button"
                            className="btn-icon btn-icon-danger"
                            onClick={() => toggleSocialLink(platform.key)}
                            data-tooltip="Remove"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Smart CTA Section */}
              <div className="bio-editor-section">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div>
                    <h3 className="bio-editor-section-title" style={{ marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}><FiZap /> Featured Smart CTA</h3>
                    <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                      Surface one primary action and route visitors by device or country.
                    </p>
                  </div>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
                    <input
                      type="checkbox"
                      checked={smartCta.enabled}
                      onChange={(e) => setSmartCta({ ...smartCta, enabled: e.target.checked })}
                    />
                    Enabled
                  </label>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={smartCta.title}
                    onChange={(e) => setSmartCta({ ...smartCta, title: e.target.value })}
                    placeholder="Featured action"
                  />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                    {TEMPLATE_TAGS.map((item) => (
                      <button key={`cta-title-${item.tag}`} type="button" className="btn-secondary" style={{ padding: "6px 10px", height: "auto", fontSize: 12 }} onClick={() => insertSmartCtaTag("title", item.tag)}>
                        {item.tag}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={smartCta.description}
                    onChange={(e) => setSmartCta({ ...smartCta, description: e.target.value })}
                    rows={2}
                    placeholder="A short prompt that explains why this destination matters"
                    style={{ resize: "vertical", padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 6, width: "100%", fontSize: 14 }}
                  />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                    {TEMPLATE_TAGS.map((item) => (
                      <button key={`cta-desc-${item.tag}`} type="button" className="btn-secondary" style={{ padding: "6px 10px", height: "auto", fontSize: 12 }} onClick={() => insertSmartCtaTag("description", item.tag)}>
                        {item.tag}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Button Label</label>
                  <input
                    type="text"
                    value={smartCta.button_label}
                    onChange={(e) => setSmartCta({ ...smartCta, button_label: e.target.value })}
                    placeholder="Open"
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div className="form-group">
                    <label>Default URL</label>
                    <input
                      type="url"
                      value={smartCta.default_url}
                      onChange={(e) => setSmartCta({ ...smartCta, default_url: e.target.value })}
                      placeholder="https://example.com"
                    />
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                      {TEMPLATE_TAGS.map((item) => (
                        <button key={`cta-default-${item.tag}`} type="button" className="btn-secondary" style={{ padding: "6px 10px", height: "auto", fontSize: 12 }} onClick={() => insertSmartCtaTag("default_url", item.tag)}>
                          {item.tag}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Mobile URL</label>
                    <input
                      type="url"
                      value={smartCta.mobile_url}
                      onChange={(e) => setSmartCta({ ...smartCta, mobile_url: e.target.value })}
                      placeholder="https://m.example.com"
                    />
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                      {TEMPLATE_TAGS.map((item) => (
                        <button key={`cta-mobile-${item.tag}`} type="button" className="btn-secondary" style={{ padding: "6px 10px", height: "auto", fontSize: 12 }} onClick={() => insertSmartCtaTag("mobile_url", item.tag)}>
                          {item.tag}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600 }}>Country overrides</h4>
                    <button type="button" className="btn-secondary" onClick={addSmartCtaOverride} style={{ padding: "8px 12px", height: "auto" }}>
                      <FiPlus /> Add rule
                    </button>
                  </div>
                  {(smartCta.country_overrides || []).length === 0 ? (
                    <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>Use overrides for country-specific destinations.</p>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {smartCta.country_overrides.map((override, index) => (
                        <div key={index} style={{ display: "grid", gridTemplateColumns: "90px 1fr auto", gap: 10, alignItems: "center" }}>
                          <select
                            value={override.country}
                            onChange={(e) => updateSmartCtaOverride(index, "country", e.target.value)}
                            style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 14 }}
                          >
                            {COUNTRY_OPTIONS.map((country) => (
                              <option key={country.code} value={country.code}>{country.name} ({country.code})</option>
                            ))}
                          </select>
                          <input
                            type="url"
                            value={override.url}
                            onChange={(e) => updateSmartCtaOverride(index, "url", e.target.value)}
                            placeholder="https://example.com/us"
                          />
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, gridColumn: "1 / span 2", marginTop: 2 }}>
                            {TEMPLATE_TAGS.map((item) => (
                              <button key={`cta-override-${index}-${item.tag}`} type="button" className="btn-secondary" style={{ padding: "6px 10px", height: "auto", fontSize: 12 }} onClick={() => insertSmartCtaOverrideTag(index, item.tag)}>
                                {item.tag}
                              </button>
                            ))}
                          </div>
                          <button type="button" className="btn-icon btn-icon-danger" onClick={() => removeSmartCtaOverride(index)} data-tooltip="Remove">
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Lead Capture Section */}
              <div className="bio-editor-section">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div>
                    <h3 className="bio-editor-section-title" style={{ marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}><FiMail /> Email Capture</h3>
                    <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                      Capture subscribers directly from the bio page.
                    </p>
                  </div>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
                    <input
                      type="checkbox"
                      checked={leadCapture.enabled}
                      onChange={(e) => setLeadCapture({ ...leadCapture, enabled: e.target.checked })}
                    />
                    Enabled
                  </label>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={leadCapture.title}
                    onChange={(e) => setLeadCapture({ ...leadCapture, title: e.target.value })}
                    placeholder="Stay in the loop"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={leadCapture.description}
                    onChange={(e) => setLeadCapture({ ...leadCapture, description: e.target.value })}
                    rows={2}
                    placeholder="Tell people why they should subscribe"
                    style={{ resize: "vertical", padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 6, width: "100%", fontSize: 14 }}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div className="form-group">
                    <label>Button Label</label>
                    <input
                      type="text"
                      value={leadCapture.button_label}
                      onChange={(e) => setLeadCapture({ ...leadCapture, button_label: e.target.value })}
                      placeholder="Join"
                    />
                  </div>
                  <div className="form-group">
                    <label>Success Message</label>
                    <input
                      type="text"
                      value={leadCapture.success_message}
                      onChange={(e) => setLeadCapture({ ...leadCapture, success_message: e.target.value })}
                      placeholder="Thanks, you're in."
                    />
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Colors</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Card Background</label>
                      <div className="color-picker-group">
                        <div className="color-picker-swatch" style={{ background: leadCapture.cardBackgroundColor }}>
                          <input type="color" value={leadCapture.cardBackgroundColor} onChange={(e) => setLeadCapture({ ...leadCapture, cardBackgroundColor: e.target.value })} />
                        </div>
                        <input type="text" value={leadCapture.cardBackgroundColor} onChange={(e) => setLeadCapture({ ...leadCapture, cardBackgroundColor: e.target.value })} className="color-picker-hex" />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Title Text</label>
                      <div className="color-picker-group">
                        <div className="color-picker-swatch" style={{ background: leadCapture.titleColor }}>
                          <input type="color" value={leadCapture.titleColor} onChange={(e) => setLeadCapture({ ...leadCapture, titleColor: e.target.value })} />
                        </div>
                        <input type="text" value={leadCapture.titleColor} onChange={(e) => setLeadCapture({ ...leadCapture, titleColor: e.target.value })} className="color-picker-hex" />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Description Text</label>
                      <div className="color-picker-group">
                        <div className="color-picker-swatch" style={{ background: leadCapture.descriptionColor }}>
                          <input type="color" value={leadCapture.descriptionColor} onChange={(e) => setLeadCapture({ ...leadCapture, descriptionColor: e.target.value })} />
                        </div>
                        <input type="text" value={leadCapture.descriptionColor} onChange={(e) => setLeadCapture({ ...leadCapture, descriptionColor: e.target.value })} className="color-picker-hex" />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Input Text</label>
                      <div className="color-picker-group">
                        <div className="color-picker-swatch" style={{ background: leadCapture.inputTextColor }}>
                          <input type="color" value={leadCapture.inputTextColor} onChange={(e) => setLeadCapture({ ...leadCapture, inputTextColor: e.target.value })} />
                        </div>
                        <input type="text" value={leadCapture.inputTextColor} onChange={(e) => setLeadCapture({ ...leadCapture, inputTextColor: e.target.value })} className="color-picker-hex" />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Input Background</label>
                      <div className="color-picker-group">
                        <div className="color-picker-swatch" style={{ background: leadCapture.inputBackgroundColor }}>
                          <input type="color" value={leadCapture.inputBackgroundColor} onChange={(e) => setLeadCapture({ ...leadCapture, inputBackgroundColor: e.target.value })} />
                        </div>
                        <input type="text" value={leadCapture.inputBackgroundColor} onChange={(e) => setLeadCapture({ ...leadCapture, inputBackgroundColor: e.target.value })} className="color-picker-hex" />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Input Border</label>
                      <div className="color-picker-group">
                        <div className="color-picker-swatch" style={{ background: leadCapture.inputBorderColor }}>
                          <input type="color" value={leadCapture.inputBorderColor} onChange={(e) => setLeadCapture({ ...leadCapture, inputBorderColor: e.target.value })} />
                        </div>
                        <input type="text" value={leadCapture.inputBorderColor} onChange={(e) => setLeadCapture({ ...leadCapture, inputBorderColor: e.target.value })} className="color-picker-hex" />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Button Color</label>
                      <div className="color-picker-group">
                        <div className="color-picker-swatch" style={{ background: leadCapture.buttonColor }}>
                          <input type="color" value={leadCapture.buttonColor} onChange={(e) => setLeadCapture({ ...leadCapture, buttonColor: e.target.value })} />
                        </div>
                        <input type="text" value={leadCapture.buttonColor} onChange={(e) => setLeadCapture({ ...leadCapture, buttonColor: e.target.value })} className="color-picker-hex" />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Button Text</label>
                      <div className="color-picker-group">
                        <div className="color-picker-swatch" style={{ background: leadCapture.buttonTextColor }}>
                          <input type="color" value={leadCapture.buttonTextColor} onChange={(e) => setLeadCapture({ ...leadCapture, buttonTextColor: e.target.value })} />
                        </div>
                        <input type="text" value={leadCapture.buttonTextColor} onChange={(e) => setLeadCapture({ ...leadCapture, buttonTextColor: e.target.value })} className="color-picker-hex" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "design" && (
            <>
              {/* Profile Section */}
              <div className="bio-editor-section">
                <h3 className="bio-editor-section-title">Profile</h3>
                
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
                  <div style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: avatarUrl ? `url(${avatarUrl}) center/cover` : "var(--surface-hover)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 32,
                    border: "2px solid var(--border)"
                  }}>
                    {!avatarUrl && "👤"}
                  </div>
                  <div>
                    <input
                      type="url"
                      value={avatarUrl}
                      onChange={(e) => setAvatarUrl(e.target.value)}
                      placeholder="Paste image URL..."
                      style={{ 
                        width: 280,
                        padding: "8px 12px", 
                        border: "1px solid var(--border)", 
                        borderRadius: 6, 
                        fontSize: 14,
                        marginBottom: 8
                      }}
                    />
                    <p style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                      Paste a URL to your profile image
                    </p>
                  </div>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Your Name or Brand"
                    maxLength={100}
                    style={{ fontSize: 16, fontWeight: 500 }}
                  />
                  <div style={{ textAlign: "right", fontSize: 11, color: "var(--text-secondary)" }}>
                    {title.length}/100
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="A short bio or tagline..."
                    rows={2}
                    maxLength={500}
                    style={{ resize: "vertical", padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 6, width: "100%", fontSize: 14 }}
                  />
                  <div style={{ textAlign: "right", fontSize: 11, color: "var(--text-secondary)" }}>
                    {description.length}/500
                  </div>
                </div>
              </div>

              {/* Theme Section */}
              <div className="bio-editor-section">
                <h3 className="bio-editor-section-title">Theme</h3>
                <div className="theme-circles">
                  {THEME_PRESETS.map(preset => (
                    <div
                      key={preset.id}
                      className={`theme-circle ${style.backgroundColor === preset.bg ? "selected" : ""} ${preset.id === "dark" || preset.id === "navy" || preset.id === "ocean" ? "dark-theme" : "light-theme"}`}
                      style={{ background: preset.bg, border: `2px solid ${preset.bg === "#f7f7f5" ? "#ddd" : preset.bg}` }}
                      onClick={() => applyThemePreset(preset)}
                      title={preset.id}
                    />
                  ))}
                </div>
              </div>

              {/* Button Style Section */}
              <div className="bio-editor-section">
                <h3 className="bio-editor-section-title">Button Style</h3>
                
                <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Shape</label>
                <div className="button-shapes" style={{ marginBottom: 20 }}>
                  {BUTTON_SHAPES.map(shape => (
                    <div
                      key={shape.id}
                      className={`button-shape ${style.buttonShape === shape.id ? "selected" : ""}`}
                      style={{ 
                        borderRadius: shape.radius, 
                        background: style.buttonColor,
                      }}
                      onClick={() => setStyle({ ...style, buttonShape: shape.id })}
                    />
                  ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Button Color</label>
                    <div className="color-picker-group">
                      <div className="color-picker-swatch" style={{ background: style.buttonColor }}>
                        <input
                          type="color"
                          value={style.buttonColor}
                          onChange={(e) => setStyle({ ...style, buttonColor: e.target.value })}
                        />
                      </div>
                      <input
                        type="text"
                        value={style.buttonColor}
                        onChange={(e) => setStyle({ ...style, buttonColor: e.target.value })}
                        className="color-picker-hex"
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Button Text</label>
                    <div className="color-picker-group">
                      <div className="color-picker-swatch" style={{ background: style.buttonTextColor }}>
                        <input
                          type="color"
                          value={style.buttonTextColor}
                          onChange={(e) => setStyle({ ...style, buttonTextColor: e.target.value })}
                        />
                      </div>
                      <input
                        type="text"
                        value={style.buttonTextColor}
                        onChange={(e) => setStyle({ ...style, buttonTextColor: e.target.value })}
                        className="color-picker-hex"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Background Section */}
              <div className="bio-editor-section">
                <h3 className="bio-editor-section-title">Background</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Color</label>
                    <div className="color-picker-group">
                      <div className="color-picker-swatch" style={{ background: style.backgroundColor }}>
                        <input
                          type="color"
                          value={style.backgroundColor}
                          onChange={(e) => setStyle({ ...style, backgroundColor: e.target.value })}
                        />
                      </div>
                      <input
                        type="text"
                        value={style.backgroundColor}
                        onChange={(e) => setStyle({ ...style, backgroundColor: e.target.value })}
                        className="color-picker-hex"
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, display: "block" }}>Text Color</label>
                    <div className="color-picker-group">
                      <div className="color-picker-swatch" style={{ background: style.textColor }}>
                        <input
                          type="color"
                          value={style.textColor}
                          onChange={(e) => setStyle({ ...style, textColor: e.target.value })}
                        />
                      </div>
                      <input
                        type="text"
                        value={style.textColor}
                        onChange={(e) => setStyle({ ...style, textColor: e.target.value })}
                        className="color-picker-hex"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "settings" && (
            <>
              {/* URL Settings */}
              <div className="bio-editor-section">
                <h3 className="bio-editor-section-title">Page URL</h3>
                
                <div className="form-group">
                  <label>Slug *</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "var(--text-secondary)", fontSize: 14, whiteSpace: "nowrap" }}>
                      {domain ? `https://${domain}/` : `${getBaseUrl().replace(/^https?:\/\//, "")}/`}
                    </span>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                      placeholder="yourname"
                      required
                      style={{ flex: 1 }}
                    />
                  </div>
                  <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    3-50 characters. Letters, numbers, hyphens, and underscores only.
                  </span>
                </div>

                <div className="form-group" style={{ marginTop: 16 }}>
                  <label>Custom Domain</label>
                  <select 
                    value={domain} 
                    onChange={(e) => setDomain(e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, fontSize: 14 }}
                  >
                    <option value="">Default (sqir.link)</option>
                    {domains.map(d => (
                      <option key={d.$id} value={d.domain}>{d.domain}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bio-editor-section">
                <h3 className="bio-editor-section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}><FiGrid /> QR Code</h3>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
                  Print or share a branded QR code that points to the live bio page.
                </p>
                <div ref={qrPreviewRef} style={{ display: "grid", placeItems: "center", padding: 20, border: "1px solid var(--border)", borderRadius: 14, background: "#fff", marginBottom: 16 }}>
                  <QRCodeCanvas value={getPreviewUrl()} size={180} includeMargin fgColor={style.buttonColor || "#37352f"} bgColor="#ffffff" />
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button type="button" className="btn-secondary" onClick={copyPreviewUrl} style={{ padding: "10px 14px", height: "auto" }}>
                    <FiCopy /> Copy URL
                  </button>
                  <button type="button" className="btn-secondary" onClick={downloadQrCode} style={{ padding: "10px 14px", height: "auto" }}>
                    <FiDownload /> Download PNG
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Preview Panel */}
        <div className="bio-editor-preview-panel">
          <div className="bio-preview-header">
            <span>Preview</span>
            <span className="bio-preview-badge">Draft</span>
          </div>
          
          <div className="bio-preview-device">
            <div 
              className="bio-preview-screen"
              style={{ background: style.backgroundColor }}
            >
              <BioPagePreview
                title={title}
                description={description}
                avatarUrl={avatarUrl}
                links={links.filter(l => l.enabled && l.title)}
                socialLinks={socialLinks}
                style={style}
                smartCta={smartCta}
                leadCapture={leadCapture}
              />
            </div>
          </div>

          {slug && (
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <a 
                href={getPreviewUrl()} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-secondary"
                style={{ fontSize: 13 }}
              >
                <FiExternalLink /> Open Live Page
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BioPagePreview({ title, description, avatarUrl, links, socialLinks, style, smartCta, leadCapture }) {
  const buttonRadius = {
    "rounded-none": "0",
    "rounded-sm": "4px",
    "rounded-md": "8px",
    "rounded-lg": "12px",
    "rounded-full": "9999px",
  }[style.buttonShape] || "8px";

  const groupedLinks = links.reduce((groups, link) => {
    const section = (link.section || "").trim();
    const key = section || "__default__";
    if (!groups[key]) groups[key] = [];
    groups[key].push(link);
    return groups;
  }, {});

  const leadCaptureTheme = {
    cardBackgroundColor: leadCapture?.cardBackgroundColor || (style.backgroundColor === "#191919" ? "#252525" : "#ffffff"),
    titleColor: leadCapture?.titleColor || style.textColor,
    descriptionColor: leadCapture?.descriptionColor || `${style.textColor}99`,
    inputBackgroundColor: leadCapture?.inputBackgroundColor || (style.backgroundColor === "#191919" ? "#191919" : "#f7f7f5"),
    inputTextColor: leadCapture?.inputTextColor || style.textColor,
    inputBorderColor: leadCapture?.inputBorderColor || `${style.textColor}22`,
    buttonColor: leadCapture?.buttonColor || style.buttonColor,
    buttonTextColor: leadCapture?.buttonTextColor || style.buttonTextColor,
  };

  return (
    <div style={{
      padding: "32px 20px",
      minHeight: 560,
      fontFamily: "ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
      color: style.textColor,
    }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            style={{ 
              width: 88, 
              height: 88, 
              borderRadius: "50%", 
              objectFit: "cover", 
              border: `3px solid ${style.textColor}20` 
            }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div style={{
            width: 88,
            height: 88,
            borderRadius: "50%",
            background: `${style.textColor}15`,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 36,
            color: style.textColor,
          }}>
            👤
          </div>
        )}
      </div>

      <h2 style={{ textAlign: "center", color: style.textColor, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
        {title || "Your Name"}
      </h2>

      {(description || !title) && (
        <p style={{ textAlign: "center", color: style.textColor, opacity: 0.8, fontSize: 14, marginBottom: 18, lineHeight: 1.5 }}>
          {description || "Your bio goes here"}
        </p>
      )}

      {smartCta?.enabled && (
        <div style={{
          marginBottom: 14,
          padding: 14,
          borderRadius: 18,
          border: "1px solid rgba(255,255,255,0.2)",
          background: `${style.buttonColor}12`,
        }}>
          {smartCta.title && <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{smartCta.title}</div>}
          {smartCta.description && <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>{smartCta.description}</div>}
          <div style={{
            padding: "12px 14px",
            background: style.buttonColor,
            color: style.buttonTextColor,
            borderRadius: buttonRadius,
            textAlign: "center",
            fontSize: 13,
            fontWeight: 600,
          }}>
            {smartCta.button_label || "Open"}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 18 }}>
        {links.length === 0 ? (
          <>
            <div style={{ padding: "14px 16px", background: style.buttonColor, color: style.buttonTextColor, borderRadius: buttonRadius, textAlign: "center", fontSize: 14, fontWeight: 500 }}>
              Your first link
            </div>
            <div style={{ padding: "14px 16px", background: style.buttonColor, color: style.buttonTextColor, borderRadius: buttonRadius, textAlign: "center", fontSize: 14, fontWeight: 500, opacity: 0.7 }}>
              Add more links
            </div>
          </>
        ) : Object.entries(groupedLinks).map(([section, sectionLinks]) => (
          <div key={section} style={{
            padding: 14,
            borderRadius: 18,
            background: `${style.textColor}08`,
            border: `1px solid ${style.textColor}14`,
          }}>
            {section !== "__default__" && (
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.04, textTransform: "uppercase", opacity: 0.7, marginBottom: 10 }}>
                {section}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {sectionLinks.map((link, i) => (
                <div key={i} style={{
                  padding: "14px 16px",
                  background: style.buttonColor,
                  color: style.buttonTextColor,
                  borderRadius: buttonRadius,
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {link.title}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {leadCapture?.enabled && (
        <div style={{
          padding: 16,
          borderRadius: 18,
          background: leadCaptureTheme.cardBackgroundColor,
          border: `1px solid ${leadCaptureTheme.inputBorderColor}`,
          marginBottom: 18,
        }}>
          {leadCapture.title && <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: leadCaptureTheme.titleColor }}>{leadCapture.title}</div>}
          {leadCapture.description && <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 12, lineHeight: 1.5, color: leadCaptureTheme.descriptionColor }}>{leadCapture.description}</div>}
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ padding: "12px 14px", borderRadius: 12, background: leadCaptureTheme.inputBackgroundColor, border: `1px solid ${leadCaptureTheme.inputBorderColor}`, color: leadCaptureTheme.inputTextColor, fontSize: 13 }}>
              Name
            </div>
            <div style={{ padding: "12px 14px", borderRadius: 12, background: leadCaptureTheme.inputBackgroundColor, border: `1px solid ${leadCaptureTheme.inputBorderColor}`, color: leadCaptureTheme.inputTextColor, fontSize: 13 }}>
              Email address
            </div>
            <div style={{ padding: "12px 14px", borderRadius: buttonRadius, background: leadCaptureTheme.buttonColor, color: leadCaptureTheme.buttonTextColor, textAlign: "center", fontSize: 14, fontWeight: 600 }}>
              {leadCapture.button_label || "Join"}
            </div>
          </div>
        </div>
      )}

      {Object.entries(socialLinks).some(([_, url]) => true) && (
        <div style={{ display: "flex", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
          {SOCIAL_PLATFORMS.filter(p => socialLinks[p.key] !== undefined).map(platform => {
            const Icon = platform.icon;
            return (
              <div
                key={platform.key}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: `${style.textColor}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: style.textColor,
                }}
              >
                <Icon size={16} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
